import { hasScope, scopeDraftFromParams } from '../GscTools/url';
import { COMPARISON, COVERAGE, HOURLY_SEARCH_TYPE } from './hours';

/**
 * The scope this report runs under, which is smaller than every other GSC tool's.
 *
 * There is no window to choose. The report is about the last 24 hours Search Console has, and
 * which hours those are is in the response rather than in a date picker — so the only things the
 * user decides are the property, whether to compare against the same hours a week earlier, and
 * whether the trailing still-collecting hours are drawn.
 *
 * The surface is not a choice either: Search Console reports hourly data for Web Search and
 * nothing else, so it is pinned and stated rather than offered.
 *
 * The shared scope resolver is deliberately not used. It exists to validate a date range and a
 * baseline, and this report has neither; passing it invented dates would make the scope bar, the
 * coverage block and the export all describe a window nobody asked for.
 */
export function initialDraft() {
    return {
        siteUrl: '',
        permissionLevel: '',
        comparison: COMPARISON.WEEK_AGO,
        coverage: COVERAGE.COMPARABLE,
    };
}

export function resolveReportScope(draft, t) {
    const errors = [];
    const warnings = [];

    if (!draft.siteUrl) errors.push(t('tools.gsc.errors.no_property'));

    const comparison = Object.values(COMPARISON).includes(draft.comparison)
        ? draft.comparison
        : COMPARISON.WEEK_AGO;
    const coverage = Object.values(COVERAGE).includes(draft.coverage)
        ? draft.coverage
        : COVERAGE.COMPARABLE;

    // Said in the scope bar rather than only beside the chart: it changes what the totals mean,
    // and the user chose it two controls above where the consequence shows up.
    if (coverage === COVERAGE.ALL) {
        warnings.push(t('tools.latest_hours.warnings.incomplete_included'));
    }

    return {
        siteUrl: draft.siteUrl,
        permissionLevel: draft.permissionLevel,
        // Fixed, not drafted — but carried on the scope because every request and every export
        // line has to name the surface it read.
        searchType: HOURLY_SEARCH_TYPE,
        comparison,
        coverage,
        comparing: comparison === COMPARISON.WEEK_AGO,
        errors,
        warnings,
    };
}

/**
 * The query-string keys this report owns.
 *
 * Only three. The shared list is not reused: a link carrying dates, a country or a device would
 * otherwise be written back out of a report that never offered those filters.
 */
export const URL_KEYS = ['site', 'compare', 'coverage'];

export function hasReportScope(params) {
    return hasScope(params, ['compare', 'coverage']);
}

/** The draft this report starts from when its address already describes one. */
export function reportDraftFromParams(params) {
    const draft = {};

    // The property is read through the shared reader so one parameter name means one thing
    // across every GSC tool.
    const { siteUrl } = scopeDraftFromParams(params);
    if (siteUrl) draft.siteUrl = siteUrl;

    if (Object.values(COMPARISON).includes(params.compare)) draft.comparison = params.compare;
    if (Object.values(COVERAGE).includes(params.coverage)) draft.coverage = params.coverage;

    return draft;
}

/** The applied scope as query parameters. */
export function reportParamsFromScope(scope) {
    return {
        site: scope.siteUrl,
        compare: scope.comparison,
        coverage: scope.coverage,
    };
}
